import React, { useEffect, useCallback, useState, useRef } from 'react'
import { useParams, useHistory, useLocation } from 'react-router-dom'
import className from 'classnames/bind'

import routesConstants from 'app/constants/routes.constants'
import useSound from 'app/components/sounds/hooks/use-sound.hook'

import Modal from 'app/components/modal/modal.component'
import SoundPopupMain from 'app/components/sounds/sound-popup/sound-popup-main/sound-popup-main.component'
import SoundPopupVideoPlayer from 'app/components/sounds/sound-popup/sound-popup-video-player/sound-popup-video-player.component'
import { EpubReaderIcons } from 'app/components/icons'

import stylesModule from 'app/components/sounds/sound-popup/sound-popup-container/sound-popup-container.module.scss'
import 'app/components/video-player/plyr-video.scss'
import Button from 'app/components/common/button/button.component'
import MultimediaEventService from 'app/services/analitics/multimedia.event.service'
import { getSoundsCategory } from 'app/utils/sounds'
import { SOUNDS_TYPE } from 'app/constants/sounds.constants'

const cx = className.bind(stylesModule)

const SoundPopupContainer = () => {
  const history = useHistory()
  const { id } = useParams<{ id: string }>()
  const { pathname } = useLocation()

  const soundCategory = getSoundsCategory(pathname)

  const desiredSound = useSound(Number(id), soundCategory)

  const [isVideoVisible, setIsVideoVisible] = useState(false)
  const [isSongVisible, setIsSongVisible] = useState(false)

  const goBackButtonRef = useRef<HTMLButtonElement>(null)

  const onShowVideo = () => {
    if (!desiredSound) return

    MultimediaEventService.sendSoundModalOpenedEvent(
      desiredSound.id,
      desiredSound.name,
      'sounds_video'
    )
    setIsVideoVisible(true)
    setIsSongVisible(false)
  }

  const onShowSong = () => {
    if (!desiredSound) return

    MultimediaEventService.sendSoundModalOpenedEvent(
      desiredSound.id,
      desiredSound.name,
      'sounds_song'
    )
    setIsSongVisible(true)
    setIsVideoVisible(false)
  }

  const onGoBack = () => {
    if (!desiredSound) return

    MultimediaEventService.sendSoundModalGoBackEvent(
      desiredSound.id,
      desiredSound.name,
      isSongVisible
    )
    setIsVideoVisible(false)
    setIsSongVisible(false)
  }

  const focusOnOpener = () => {
    const openerElement = document.querySelector(`#sound-item-${id} > div`)
    if (openerElement instanceof HTMLElement) {
      openerElement.focus()
    }
  }

  const handleClose = useCallback(() => {
    if (soundCategory === SOUNDS_TYPE.SOUNDS) {
      history.push(routesConstants.SOUNDS)
    } else {
      history.push(routesConstants.SONIDOS)
    }
    focusOnOpener()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, desiredSound, isSongVisible, isVideoVisible])

  const grayoutBackground = () => {
    document.body.style.overflow = 'hidden'

    return function cleanup() {
      document.body.style.overflow = 'initial'
    }
  }

  const sendCloseEvent = () => {
    if (!desiredSound) return
    return () =>
      MultimediaEventService.sendSoundModalCloseEvent(
        desiredSound.id,
        desiredSound.name,
        isSongVisible,
        isVideoVisible
      )
  }

  useEffect(grayoutBackground, [])
  useEffect(sendCloseEvent, [desiredSound, isVideoVisible, isSongVisible])

  useEffect(() => {
    if (isVideoVisible || isSongVisible) {
      goBackButtonRef.current?.focus()
    }
  }, [isVideoVisible, isSongVisible])

  return desiredSound ? (
    <Modal
      selectors="button, div.plyr, .plyr__controls *"
      close={handleClose}
      ariaLabel={desiredSound.soundname}
      classNameList={{
        wrapper: cx('sound-popup'),
        main: cx('sound-popup__main'),
        closeButton: cx('sound-popup__close-button'),
      }}
    >
      {isVideoVisible || isSongVisible ? (
        <>
          <Button
            ref={goBackButtonRef}
            onClick={onGoBack}
            className={cx('go-back-button')}
            aria-label="go back"
          >
            <EpubReaderIcons.FatBackArrowIcon />
          </Button>
          <SoundPopupVideoPlayer
            sound={desiredSound}
            isSongVisible={isSongVisible}
          />
        </>
      ) : (
        <SoundPopupMain
          sound={desiredSound}
          onSongClick={onShowSong}
          onVideoClick={onShowVideo}
        />
      )}
    </Modal>
  ) : null
}

export default SoundPopupContainer
